import { FC, useContext, useMemo, useState } from 'react';
import {
  CommentFragment,
  LoadCommentsQuery,
  LoadCommentsQueryVariables,
  ParagraphCommentListAndFormularFragment,
} from '../../../generated/types';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Heading,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { CommentForm } from '../../Forms/CommentForm';
import { Comment } from '../../Common/Comment';
import { useLazyQuery } from '@apollo/client';
import { QueryLoadComments } from '../../../graphql/LoadMoreCommentsQuery';
import { PageContext } from '../../../context/PageContext';

interface CommentListAndFormularProps {
  data: ParagraphCommentListAndFormularFragment;
}

export type CommentWithChildren = CommentFragment & {
  children: CommentWithChildren[];
};

export const CommentListAndFormular: FC<CommentListAndFormularProps> = ({
  data,
}) => {
  const [comments, setComments] = useState(data.comments);
  const pageContext = useContext(PageContext);
  const [loadComments, { loading, data: loadCommentsData, error }] =
    useLazyQuery<LoadCommentsQuery, LoadCommentsQueryVariables>(
      QueryLoadComments,
      {}
    );

  const groupedComments = useMemo(() => {
    const map = new Map<number, CommentWithChildren>();
    const groups: CommentWithChildren[] = [];

    comments.forEach((comment) => {
      map.set(comment.id, {
        ...comment,
        children: [],
      });
    });

    comments.forEach((comment) => {
      const commentItem = map.get(comment.id)!;

      if (comment.parentComment?.id && map.has(comment.parentComment.id)) {
        map.get(comment.parentComment.id)!.children!.push(commentItem);
      } else {
        groups.push(commentItem);
      }
    });

    return groups;
  }, [comments]);

  const onLoadMore = async () => {
    const result = await loadComments({
      variables: {
        entityId: pageContext.entity!.id,
        offset: comments.length,
      },
    });

    if (result.data?.loadMoreComments) {
      setComments([...comments, ...result.data.loadMoreComments]);
    }
  };

  const [openCommentIdForm, setOpenCommentIdForm] = useState<number | null>(
    null
  );
  const { isOpen, onOpen, onClose } = useDisclosure();

  const toggleCommentIdForm = (id: number) => {
    onClose();

    setOpenCommentIdForm(id);
  };

  return (
    <Box borderTop="1px solid" borderTopColor="gray.300" pt={6}>
      <Heading fontSize="2xl" mb={4} fontWeight={500} as="h3" color="primary">
        Kommentare ({data.totalComments})
      </Heading>

      <VStack
        spacing={6}
        divider={<Divider borderColor="gray.200" opacity={1} />}
        color="primary"
      >
        {groupedComments.map((comment) => {
          return (
            <Comment
              data={comment}
              key={comment.id}
              onClickAnswer={toggleCommentIdForm}
              openCommentIdForm={openCommentIdForm}
            />
          );
        })}
      </VStack>

      {comments.length > 0 && (
        <Flex
          py={6}
          mt={6}
          borderTop="1px solid"
          borderTopColor="gray.100"
          color="primary"
          alignItems="center"
          flexDir="column"
        >
          {comments.length > 0 && (
            <Box fontSize="sm">
              Kommentare 1 - {comments.length} von {data.totalComments}
            </Box>
          )}
          {data.totalComments > comments.length && (
            <Button isLoading={loading} mt={2} size="sm" onClick={onLoadMore}>
              Mehr Kommentare laden
            </Button>
          )}
        </Flex>
      )}

      <Collapse in={isOpen} animateOpacity>
        <Box
          bg="gray.50"
          p={6}
          mt={9}
          border="1px solid"
          borderColor="gray.400"
        >
          {data.headline && (
            <Heading
              fontSize="2xl"
              mb={6}
              fontWeight={500}
              as="h3"
              color="primary"
            >
              {data.headline}
            </Heading>
          )}

          <CommentForm />
        </Box>
      </Collapse>
      <Collapse in={!isOpen} animateOpacity>
        <Flex justifyContent="center">
          <Button
            mt={12}
            variant="roundBlue"
            onClick={() => {
              setOpenCommentIdForm(null);
              onOpen();
            }}
          >
            Kommentar schreiben
          </Button>
        </Flex>
      </Collapse>
    </Box>
  );
};
